import { tool } from '@openai/agents/realtime';
import { VOICE_TOOL_DESCRIPTIONS } from '@/lib/live-voice-tool-definitions';
import { claimLiveVoiceToolOperation } from '@/lib/live-voice-tool-operations';
import {
  findNavigationTargetsSchema,
  navigateUiSchema,
} from '@/lib/voice-call-tool-input';
import type { RealtimeVoiceToolContext } from './realtime-voice-tool-context';

const INACTIVE_CALL =
  'Internal safety: this voice call is no longer active. Stay silent.';

export function createRealtimeVoiceNavigationTools(
  context: RealtimeVoiceToolContext
) {
  const findNavigationTargetsTool = tool({
    name: 'find_navigation_targets',
    description: VOICE_TOOL_DESCRIPTIONS.find_navigation_targets,
    parameters: findNavigationTargetsSchema,
    strict: true,
    errorFunction: null,
    execute: async (input) => {
      if (!context.isCurrentCall()) return INACTIVE_CALL;
      if (!context.activeOperationRef.current) {
        return 'Navigation discovery requires a new spoken user request.';
      }
      const result = await context.findNavigationTargets({
        orgThreadId: context.orgThreadId,
        sessionId: context.leaseSessionId,
        ...findNavigationTargetsSchema.parse(input),
      });
      if (!context.isCurrentCall()) return INACTIVE_CALL;
      return context.finishRealtimeToolResult({
        toolName: 'find_navigation_targets',
        result: JSON.stringify(result),
      });
    },
  });

  const navigateUiTool = tool({
    name: 'navigate_ui',
    description: VOICE_TOOL_DESCRIPTIONS.navigate_ui,
    parameters: navigateUiSchema,
    strict: true,
    errorFunction: null,
    execute: async (input) => {
      if (!context.isCurrentCall()) return INACTIVE_CALL;
      const parsed = navigateUiSchema.parse(input);
      const operationId = context.activeOperationRef.current;
      if (!operationId || !claimLiveVoiceToolOperation(context, 'navigation')) {
        return 'Navigation was already attempted for this request. Wait for a new user request before navigating again.';
      }
      const result = await context.navigateUi({
        orgThreadId: context.orgThreadId,
        sessionId: context.leaseSessionId,
        operationId,
        ...parsed,
      });
      if (!context.isCurrentCall()) return INACTIVE_CALL;
      const delivery = context.deliverNavigation(result.navigation);
      return context.finishRealtimeToolResult({
        toolName: 'navigate_ui',
        result: JSON.stringify({
          status: delivery,
          navigation: result.navigation,
          ...(delivery === 'blocked'
            ? {
                instruction:
                  'The browser blocked opening another tab. The link is saved in the original chat; tell the user to open it there.',
              }
            : delivery === 'invalid'
              ? {
                  instruction:
                    'The destination could not be opened safely. Do not claim navigation succeeded.',
                }
              : {}),
        }),
      });
    },
  });

  return { findNavigationTargetsTool, navigateUiTool };
}
